import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  LinearProgress,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Divider,
} from '@mui/material'
import { Add, Edit, Delete, Book, Close, Collections, FileDownload } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import {
  useGetCataloguesQuery,
  useCreateCatalogueMutation,
  useUpdateCatalogueMutation,
  useDeleteCatalogueMutation,
  type CatalogueCreate,
} from '../store/services/catalogueApi'
import { apiClient } from '../services/api'
import CatalogueExportDialog from '../components/catalogue/CatalogueExportDialog'

const Catalogues: React.FC = () => {
  const [page, setPage] = useState(1)
  const [openDialog, setOpenDialog] = useState(false)
  const [openCollectionsDialog, setOpenCollectionsDialog] = useState(false)
  const [openExportDialog, setOpenExportDialog] = useState(false)
  const [editingCatalogue, setEditingCatalogue] = useState<any>(null)
  const [selectedCatalogue, setSelectedCatalogue] = useState<any>(null)
  const [exportingCatalogue, setExportingCatalogue] = useState<any>(null)
  const [allCollections, setAllCollections] = useState<any[]>([])
  const [catalogueCollections, setCatalogueCollections] = useState<any[]>([])
  const [loadingCollections, setLoadingCollections] = useState(false)
  const [formData, setFormData] = useState<CatalogueCreate>({
    name: '',
    description: '',
    is_published: false,
  })

  const { enqueueSnackbar } = useSnackbar()
  const { data, isLoading, error, refetch } = useGetCataloguesQuery({ page, per_page: 20 })
  const [createCatalogue, { isLoading: isCreating }] = useCreateCatalogueMutation()
  const [updateCatalogue, { isLoading: isUpdating }] = useUpdateCatalogueMutation()
  const [deleteCatalogue] = useDeleteCatalogueMutation()

  // Load all collections
  useEffect(() => {
    loadAllCollections()
  }, [])

  const loadAllCollections = async () => {
    try {
      const response = await apiClient.get('/admin/collections', {
        params: { per_page: 100 }
      })
      setAllCollections(response.data.items || [])
    } catch (error) {
      console.error('Error loading collections:', error)
    }
  }

  const loadCatalogueCollections = async (catalogueId: string) => {
    setLoadingCollections(true)
    try {
      const response = await apiClient.get(`/admin/catalogues/${catalogueId}/collections`)
      setCatalogueCollections(response.data.items || [])
    } catch (error) {
      console.error('Error loading catalogue collections:', error)
      enqueueSnackbar('Failed to load catalogue collections', { variant: 'error' })
    } finally {
      setLoadingCollections(false)
    }
  }

  const handleOpenCollectionsDialog = async (catalogue: any) => {
    setSelectedCatalogue(catalogue)
    setOpenCollectionsDialog(true)
    await loadCatalogueCollections(catalogue.id)
  }

  const handleCloseCollectionsDialog = () => {
    setOpenCollectionsDialog(false)
    setSelectedCatalogue(null)
    setCatalogueCollections([])
  }

  const handleOpenExportDialog = (catalogue: any) => {
    setExportingCatalogue(catalogue)
    setOpenExportDialog(true)
  }

  const handleCloseExportDialog = () => {
    setOpenExportDialog(false)
    setExportingCatalogue(null)
  }

  const handleToggleCollection = async (collectionId: string, isCurrentlyAssigned: boolean) => {
    if (!selectedCatalogue) return

    try {
      if (isCurrentlyAssigned) {
        // Remove collection from catalogue
        await apiClient.delete(`/admin/catalogues/${selectedCatalogue.id}/collections`, {
          data: { collection_ids: [collectionId] }
        })
        enqueueSnackbar('Collection removed from catalogue', { variant: 'success' })
      } else {
        // Add collection to catalogue
        await apiClient.post(`/admin/catalogues/${selectedCatalogue.id}/collections`, {
          collection_ids: [collectionId]
        })
        enqueueSnackbar('Collection added to catalogue', { variant: 'success' })
      }
      // Reload catalogue collections
      await loadCatalogueCollections(selectedCatalogue.id)
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to update collection assignment',
        { variant: 'error' }
      )
    }
  }

  const handleOpenDialog = (catalogue?: any) => {
    if (catalogue) {
      setEditingCatalogue(catalogue)
      setFormData({
        name: catalogue.name,
        description: catalogue.description || '',
        is_published: catalogue.is_published,
      })
    } else {
      setEditingCatalogue(null)
      setFormData({ name: '', description: '', is_published: false })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingCatalogue(null)
    setFormData({ name: '', description: '', is_published: false })
  }

  const handleSubmit = async () => {
    try {
      if (editingCatalogue) {
        await updateCatalogue({ id: editingCatalogue.id, data: formData }).unwrap()
        enqueueSnackbar('Catalogue updated successfully', { variant: 'success' })
      } else {
        await createCatalogue(formData).unwrap()
        enqueueSnackbar('Catalogue created successfully', { variant: 'success' })
      }
      handleCloseDialog()
      refetch()
    } catch (err: any) {
      enqueueSnackbar(
        err?.data?.detail || 'Failed to save catalogue',
        { variant: 'error' }
      )
    }
  }

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      try {
        await deleteCatalogue(id).unwrap()
        enqueueSnackbar('Catalogue deleted successfully', { variant: 'success' })
        refetch()
      } catch (err: any) {
        enqueueSnackbar(
          err?.data?.detail || 'Failed to delete catalogue',
          { variant: 'error' }
        )
      }
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Catalogue Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Organize products into catalogues for customer-specific visibility
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
        >
          Add Catalogue
        </Button>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Failed to load catalogues
          </Typography>
          <Typography variant="body2">
            {error && 'data' in error ? JSON.stringify(error.data) : 'An error occurred while loading catalogues'}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => refetch()}
            sx={{ mt: 1 }}
          >
            Retry
          </Button>
        </Alert>
      )}

      {/* Catalogues Table */}
      {!isLoading && !error && (
        <Card>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#0d47a1' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                      <Book sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No catalogues found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Get started by creating your first catalogue
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                          bgcolor: '#f44336',
                          '&:hover': { bgcolor: '#c62828' }
                        }}
                      >
                        Create First Catalogue
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.items.map((catalogue) => (
                    <TableRow key={catalogue.id} hover>
                      <TableCell>
                        <Typography fontWeight={600}>{catalogue.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {catalogue.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {catalogue.description || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={catalogue.is_published ? 'Published' : 'Draft'}
                            color={catalogue.is_published ? 'success' : 'warning'}
                            size="small"
                            sx={{ borderRadius: 4 }}
                          />
                          {catalogue.is_featured && (
                            <Chip
                              label="Featured"
                              color="info"
                              size="small"
                              sx={{ borderRadius: 4 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenExportDialog(catalogue)}
                          sx={{ color: '#ff9800' }}
                          title="Export Catalogue"
                        >
                          <FileDownload fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCollectionsDialog(catalogue)}
                          sx={{ color: '#4caf50' }}
                          title="Manage Collections"
                        >
                          <Collections fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(catalogue)}
                          sx={{ color: '#2196f3' }}
                          title="Edit Catalogue"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(catalogue.id, catalogue.name)}
                          sx={{ color: '#f44336' }}
                          title="Delete Catalogue"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data && data.pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <Pagination
                count={data.pages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Card>
      )}

      {/* Edit Catalogue Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {(isCreating || isUpdating) && <LinearProgress />}
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#2196f3',
          color: 'white'
        }}>
          {editingCatalogue ? 'Edit Catalogue' : 'Add Catalogue'}
          <IconButton onClick={handleCloseDialog} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            fullWidth
            label="Catalogue Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
            error={!formData.name.trim()}
            helperText={!formData.name.trim() ? 'Catalogue name is required' : ''}
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            placeholder="Enter catalogue description..."
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                color="primary"
              />
            }
            label="Published"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} disabled={isCreating || isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating || !formData.name.trim()}
            sx={{
              bgcolor: '#f44336',
              '&:hover': { bgcolor: '#c62828' }
            }}
          >
            {(isCreating || isUpdating) ? 'Saving...' : (editingCatalogue ? 'Update Catalogue' : 'Create Catalogue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Collections Dialog */}
      <Dialog
        open={openCollectionsDialog}
        onClose={handleCloseCollectionsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#4caf50',
          color: 'white'
        }}>
          <Box>
            <Typography variant="h6">Manage Collections</Typography>
            <Typography variant="caption">
              {selectedCatalogue?.name}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCollectionsDialog} sx={{ color: 'inherit' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {loadingCollections ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which collections should be included in this catalogue
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {allCollections.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primary="No collections available"
                      secondary="Create collections first to assign them to catalogues"
                    />
                  </ListItem>
                ) : (
                  allCollections.map((collection) => {
                    const isAssigned = catalogueCollections.some(c => c.id === collection.id)
                    return (
                      <ListItem
                        key={collection.id}
                        button
                        onClick={() => handleToggleCollection(collection.id, isAssigned)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                      >
                        <ListItemText
                          primary={collection.name}
                          secondary={collection.description || 'No description'}
                        />
                        <ListItemSecondaryAction>
                          <Checkbox
                            edge="end"
                            checked={isAssigned}
                            onChange={() => handleToggleCollection(collection.id, isAssigned)}
                            color="success"
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    )
                  })
                )}
              </List>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" color="info.dark">
                  <strong>Selected:</strong> {catalogueCollections.length} of {allCollections.length} collections
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseCollectionsDialog} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Catalogue Dialog */}
      {exportingCatalogue && (
        <CatalogueExportDialog
          open={openExportDialog}
          catalogue={exportingCatalogue}
          onClose={handleCloseExportDialog}
        />
      )}
    </Box>
  )
}

export default Catalogues
